import axios from 'axios';
import { cleanLenderSubmissionData } from '../utils';

/**
 * Submit a contact to a lender with optional file attachments
 * 
 * @param {Object} contactData - Contact information
 * @param {Object} businessData - Business opportunity data
 * @param {string} lenderId - ID of the lender to submit to
 * @param {File[]} bankFiles - Array of bank statement files
 * @param {File[]} appFiles - Array of application document files
 * @returns {Promise<Object>} Response from the lender API
 */
export const submitToLender = async (contactData, businessData, lenderId, bankFiles = [], appFiles = []) => {
  try {
    // Create form data for file upload
    const formData = new FormData();
    
    // Clean and format the data before submission
    const cleanedData = cleanLenderSubmissionData({
      contactData,
      opportunityData: businessData // Map businessData to opportunityData for the cleaner
    });
    
    // Add contact and business data
    formData.append('contactData', JSON.stringify(cleanedData.contactData));
    formData.append('businessData', JSON.stringify(cleanedData.opportunityData));
    formData.append('lenderId', lenderId);
    
    // Separate HubSpot files from manual uploads
    const hubspotBankFiles = [];
    const manualBankFiles = [];
    const hubspotAppFiles = [];
    const manualAppFiles = [];
    
    // Process bank files
    for (const file of bankFiles) {
      if (file.isHubspotFile) {
        // HubSpot file - send metadata only
        hubspotBankFiles.push({
          id: file.id,
          name: file.name,
          url: file.url,
          size: file.size,
          extension: file.extension,
          access: file.access || 'PRIVATE'
        });
      } else {
        // Manual upload - send as File object
        manualBankFiles.push(file);
      }
    }
    
    // Process app files
    for (const file of appFiles) {
      if (file.isHubspotFile) {
        // HubSpot file - send metadata only
        hubspotAppFiles.push({
          id: file.id,
          name: file.name,
          url: file.url,
          size: file.size,
          extension: file.extension,
          access: file.access || 'PRIVATE'
        });
      } else {
        // Manual upload - send as File object
        manualAppFiles.push(file);
      }
    }
    
    // Add manual upload files as File objects
    for (const file of manualBankFiles) {
      formData.append('bankFiles', file);
    }
    
    for (const file of manualAppFiles) {
      formData.append('appFiles', file);
    }
    
    // Add HubSpot file metadata as JSON
    if (hubspotBankFiles.length > 0) {
      formData.append('hubspotBankFiles', JSON.stringify(hubspotBankFiles));
      console.log(`[lenderApi] Sending ${hubspotBankFiles.length} HubSpot bank files`);
    }
    
    if (hubspotAppFiles.length > 0) {
      formData.append('hubspotAppFiles', JSON.stringify(hubspotAppFiles));
      console.log(`[lenderApi] Sending ${hubspotAppFiles.length} HubSpot app files`);
    }
    
    console.log(`[lenderApi] Total files - Manual: ${manualBankFiles.length + manualAppFiles.length}, HubSpot: ${hubspotBankFiles.length + hubspotAppFiles.length}`);
    
    // Submit to the lender
    const response = await axios.post('/api/lenders/submit', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    // Check for existing customer status
    if (response.data.existingCustomer || response.data.status === 'existing_customer') {
      console.log('[lenderApi.js] Received existing_customer status:', response.data);
      
      // Return the response normally but add a flag to make it detectable in the UI
      return {
        ...response.data,
        existingCustomer: true,
        status: 'existing_customer',
        message: response.data.message || 'This application has already been received by the lender or another broker.'
      };
    }
    
    return response.data;
  } catch (error) {
    // --- DEBUGGING LENDER API CATCH BLOCK ---
    console.log('[lenderApi.js] Caught error:', error);
    console.log('[lenderApi.js] error.message:', error.message);
    if (error.response) {
      console.log('[lenderApi.js] error.response exists.');
      console.log('[lenderApi.js] error.response.status:', error.response.status);
      console.log('[lenderApi.js] error.response.data:', error.response.data);
      if (error.response.data) {
         console.log('[lenderApi.js] error.response.data.message:', error.response.data.message);
      } else {
        console.log('[lenderApi.js] error.response.data is missing.');
      }
    } else {
      console.log('[lenderApi.js] error.response is missing.');
    }
    // --- END DEBUGGING ---

    // Check if the server sent back a specific JSON message
    if (error.response && error.response.data && error.response.data.message) {
      console.log('[lenderApi.js] Using specific server message.');
      // Throw a new error containing ONLY the server's specific message
      throw new Error(error.response.data.message);
    } else if (error.message) {
      console.log('[lenderApi.js] Using generic error.message.');
      // If no specific server message, use the general error message
      throw new Error(error.message);
    } else {
      console.log('[lenderApi.js] Falling back to unknown error message.');
      // Fallback for unknown errors
      throw new Error('An unknown error occurred during lender submission.');
    }
  }
};

/**
 * Get lender match settings
 * 
 * @returns {Promise<Object>} Lender match settings
 */
export const getLenderMatchSettings = async () => {
  try {
    const response = await axios.get('/api/lenders/match/settings');
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error;
  }
};

/**
 * Toggle lender match enabled status
 * 
 * @param {boolean} enabled - Whether lender match should be enabled 
 * @returns {Promise<Object>} Response with updated enabled status
 */
export const toggleLenderMatch = async (enabled) => {
  try {
    const response = await axios.put('/api/lenders/match/enabled', { enabled });
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error;
  }
};

/**
 * Get all available fields for lender criteria
 * 
 * @returns {Promise<Array>} Array of available fields
 */
export const getAvailableFields = async () => {
  try {
    const response = await axios.get('/api/lenders/match/available-fields');
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error;
  }
};

/**
 * Update criteria for a specific lender
 * 
 * @param {string} lenderId - ID of the lender
 * @param {string} name - Name of the lender
 * @param {Array} criteria - Array of criteria objects
 * @returns {Promise<Object>} Response with updated lender criteria
 */
export const updateLenderCriteria = async (lenderId, name, criteria) => {
  try {
    const response = await axios.post('/api/lenders/match/lender-criteria', {
      lenderId,
      name,
      criteria
    });
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error;
  }
};

/**
 * Delete a specific criterion for a lender
 * 
 * @param {string} lenderId - ID of the lender
 * @param {number} criterionIndex - Index of the criterion to delete
 * @returns {Promise<Object>} Response with deletion confirmation
 */
export const deleteCriterion = async (lenderId, criterionIndex) => {
  try {
    const response = await axios.delete(`/api/lenders/match/lender-criteria/${lenderId}/${criterionIndex}`);
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error;
  }
};

/**
 * Calculate lender match scores for a contact
 * 
 * @param {string} contactId - ID of the contact
 * @returns {Promise<Object>} Response with match scores
 */
export const calculateMatchScores = async (contactId) => {
  try {
    const response = await axios.get(`/api/lenders/match/calculate/${contactId}`);
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error;
  }
};

/**
 * Get submission history for a specific Federal Tax ID
 * 
 * @param {string} federalTaxId - Federal Tax ID to look up
 * @returns {Promise<Object>} Map of lender IDs to submission dates
 */
export const getSubmissionHistory = async (federalTaxId) => {
  try {
    // Skip the API call if no Federal Tax ID is provided
    if (!federalTaxId) {
      console.log('No Federal Tax ID provided for submission history lookup');
      return {};
    }
    
    console.log(`Fetching submission history for Federal Tax ID: ${federalTaxId}`);
    const response = await axios.get(`/api/lenders/submission-history/${federalTaxId}`);
    
    if (response.data && response.data.success && response.data.history) {
      console.log(`Found submission history: ${Object.keys(response.data.history).length} entries`);
      return response.data.history;
    }
    
    return {};
  } catch (error) {
    console.error('Error fetching submission history:', error);
    // Don't throw, just return empty object as this is a non-critical feature
    return {};
  }
}; 